import { useEffect, useState, useRef } from "react";
import useIsMobile from "@hooks/useIsMobile";
var LOCAL_STORAGE_KEY = "highlight_overlay_last_run";
var EXPIRATION_DAYS = 30; // Number of days before it can run again

export default function useHighlightOverlay() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$selector = _ref.selector,
    selector = _ref$selector === void 0 ? ".highlightme" : _ref$selector,
    _ref$intervalTime = _ref.intervalTime,
    intervalTime = _ref$intervalTime === void 0 ? 1000 : _ref$intervalTime,
    _ref$overlayColor = _ref.overlayColor,
    overlayColor = _ref$overlayColor === void 0 ? "rgba(0, 0, 0, 0.5)" : _ref$overlayColor,
    _ref$zIndexBase = _ref.zIndexBase,
    zIndexBase = _ref$zIndexBase === void 0 ? 1000 : _ref$zIndexBase;
  var _useState = useState(false),
    isActive = _useState[0],
    setIsActive = _useState[1];
  var overlayRef = useRef(null);
  var elementsRef = useRef([]);
  var intervalRef = useRef(null);
  var isMobile = useIsMobile(); // ✅ Detect if the user is on mobile

  useEffect(function () {
    // ✅ Check local storage to prevent rerunning
    var lastRun = localStorage.getItem(LOCAL_STORAGE_KEY);
    var now = new Date().getTime();
    var expirationTime = EXPIRATION_DAYS * 24 * 60 * 60 * 1000; // Convert days to milliseconds

    if (lastRun && now - parseInt(lastRun, 10) < expirationTime) {
      return; // ✅ If the overlay was recently shown, do nothing
    }
    if (!isActive) return;

    // ✅ Save timestamp in local storage to prevent reruns
    localStorage.setItem(LOCAL_STORAGE_KEY, now.toString());

    // Create overlay dynamically
    var overlay = document.createElement("div");
    overlay.style.position = "fixed";
    overlay.style.top = "0";
    overlay.style.left = "0";
    overlay.style.width = "100vw";
    overlay.style.height = "100vh";
    overlay.style.backgroundColor = overlayColor;
    overlay.style.zIndex = "".concat(zIndexBase);
    overlay.style.pointerEvents = "auto"; // Allow clicks on overlay
    document.body.appendChild(overlay);
    overlayRef.current = overlay;

    // ✅ Click overlay to stop highlighting
    overlay.addEventListener("click", function () {
      return setIsActive(false);
    });

    // ✅ Handle stop conditions based on device type
    var handleInteraction = function handleInteraction() {
      return setIsActive(false);
    };
    if (isMobile) {
      window.addEventListener("touchstart", handleInteraction);
    } else {
      window.addEventListener("scroll", handleInteraction);
    }

    // ✅ Find only visible elements
    var allElements = Array.from(document.querySelectorAll(selector));
    var visibleElements = allElements.filter(function (el) {
      return isElementInViewport(el);
    });
    if (visibleElements.length === 0 && allElements.length > 0) {
      scrollToClosestElement(allElements);
      setTimeout(function () {
        visibleElements = allElements.filter(function (el) {
          return isElementInViewport(el);
        });
        elementsRef.current = visibleElements.length > 0 ? visibleElements : allElements;
        startHighlighting();
      }, 500);
    } else {
      elementsRef.current = visibleElements;
      startHighlighting();
    }
    function startHighlighting() {
      if (elementsRef.current.length === 0) return;
      var index = 0;
      intervalRef.current = setInterval(function () {
        elementsRef.current.forEach(function (el) {
          el.style.zIndex = "auto";
          el.style.backgroundColor = "";
          el.classList.remove("highlighted-element");
          el.style.borderRadius = "";
        });
        var currentElement = elementsRef.current[index];
        currentElement.style.zIndex = "".concat(zIndexBase + 10);
        currentElement.style.borderRadius = "5px";
        flashHighlight(currentElement);
        index = (index + 1) % elementsRef.current.length;
      }, intervalTime);
    }

    // Cleanup function
    return function () {
      clearInterval(intervalRef.current);
      if (overlayRef.current) {
        overlayRef.current.removeEventListener("click", function () {
          return setIsActive(false);
        });
        document.body.removeChild(overlayRef.current);
        overlayRef.current = null;
      }

      // ✅ Remove event listeners based on device type
      if (isMobile) {
        window.removeEventListener("touchstart", handleInteraction);
      } else {
        window.removeEventListener("scroll", handleInteraction);
      }

      // Reset styles when effect is stopped
      elementsRef.current.forEach(function (el) {
        el.style.zIndex = "auto";
        el.style.backgroundColor = "";
        el.classList.remove("highlighted-element");
        el.style.borderRadius = "";
      });
    };
  }, [isActive, selector, intervalTime, overlayColor, zIndexBase, isMobile]);

  // ✅ Utility function to check if an element is in the viewport
  function isElementInViewport(el) {
    var rect = el.getBoundingClientRect();
    var padding = 64; // Navbar height

    return rect.top >= padding && rect.left >= 0 && rect.bottom <= (window.innerHeight - padding || document.documentElement.clientHeight - padding) && rect.right <= (window.innerWidth || document.documentElement.clientWidth);
  }

  // ✅ Scroll to the closest element if none are visible
  function scrollToClosestElement(elements) {
    var extraScrollOffset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 130;
    if (elements.length === 0) return;
    var viewportCenterY = window.innerHeight / 2;
    var closestElement = elements[0];
    var minDistance = Math.abs(elements[0].getBoundingClientRect().top - viewportCenterY);
    elements.forEach(function (el) {
      var distance = Math.abs(el.getBoundingClientRect().top - viewportCenterY);
      if (distance < minDistance) {
        closestElement = el;
        minDistance = distance;
      }
    });
    closestElement.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
    setTimeout(function () {
      window.scrollBy({
        top: extraScrollOffset,
        behavior: "smooth"
      });
    }, 300);
  }

  // ✅ Flash highlight effect
  function flashHighlight(el) {
    el.classList.add("highlighted-element");
    el.style.backgroundColor = "rgb(255, 255, 255)";
  }
  return {
    start: function start() {
      return setIsActive(true);
    },
    stop: function stop() {
      setIsActive(false);
      elementsRef.current.forEach(function (el) {
        return el.classList.remove("highlighted-element");
      });
    },
    isActive: isActive
  };
}